import { createTheme } from '@mui/material/styles';

// 1. Tell TypeScript that 'navigation' is a valid part of the Theme
declare module '@mui/material/styles' {
  interface TypographyVariants {
    breadcrumbLink: React.CSSProperties;
    navTab: React.CSSProperties;
    employeeName: React.CSSProperties;
    fontWeightSemiBold: number; // 600 — between MUI's medium (500) and bold (700)
    fontSizeTitle: number; // 15px — card/section title
    fontSizeDescription: number; // 13px — descriptive body text
    fontSizeLabel: number; // 11px — eyebrow/overline uppercase labels
  }
  interface TypographyVariantsOptions {
    breadcrumbLink?: React.CSSProperties;
    navTab?: React.CSSProperties;
    employeeName?: React.CSSProperties;
    fontWeightSemiBold?: number;
    fontSizeTitle?: number;
    fontSizeDescription?: number;
    fontSizeLabel?: number;
  }

  interface Theme {
    toolbar: { height: number };
    navigation: {
      main: { hover: string; selected: string; selectedAccent: string; selectedBorder: string };
      subMenu: { hover: string; selected: string; selectedAccent: string; selectedBorder: string };
    };
    avatar: {
      sm: number; // 32px — inline/chip contexts
      md: number; // 40px — default MUI size
      lg: number; // 55px — header logo, prominent placements
    };
    iconSize: {
      xxs: number; // 12px — legend / category square markers
      xs: number; // 14px — inline button spinners
      sm: number; // 20px — section card badge icons
      md: number; // 24px — small icon buttons
      lg: number; // 56px — large icon containers (empty states)
    };
    chip: {
      sm: number; // 22px — small chip/badge height
    };
    button: {
      sm: number; // 30px — small inline button
      md: number; // 44px — action button height in dialogs
    };
    /** Compact global-search field above a datatable (toolbar), e.g. the users list. */
    searchField: {
      width: number; // px — field width
      height: number; // px — field height (default MUI small input is 40px)
      fontSize: string; // input text + placeholder size
      color: string; // input text + placeholder colour (slate 900)
    };
    /** Sizing tokens for the shared SubNavBar (tab-style navigation). */
    subNav: {
      indicatorHeight: number; // px — active-tab underline thickness
      size: Record<
        'medium' | 'small',
        { fontSize: string; px: number; py: number; minHeight: number | 'auto' }
      >;
    };
    borderRadius: {
      pill: string; // '9999px' — fully rounded pill shape
      xs: string; //  '6px' — small buttons, icon boxes
      sm: string; //  '8px' — small containers, key row
      md: string; // '12px' — cards, banners
      lg: string; // '16px' — modals, large containers
      xl: string; // '18px' — confirm overlay card
    };
    /** Layout tokens shared by all detail pages (employee, contract, …) */
    detailPage: {
      containerPx: number;
      containerPt: number;
      containerPb: number;
      gap: number;
      gridCols: { xs: string; lg: string };
      tableMaxHeight: number;
      historyTableMaxHeight: number;
    };
    /** Tokens for the filter panel (sizing + active-criteria chip styling). */
    filters: {
      /** px — default width of the panel date operator select, fits "≥ (à partir du)". */
      operatorSelectWidth: number;
      /** Text + border colour of the active-criteria chips. */
      chipColor: string;
      /** Background colour of the active-criteria chips. */
      chipBgColor: string;
    };
    /** Tokens for the centered auth card shell (Login / Forgot password). */
    authCard: {
      /** px — max width of the card. */
      maxWidth: number;
      /** px — display width of the logo. */
      logoWidth: number;
    };
    /** Sizing + type scale for the landing HomePage welcome card. */
    homePage: {
      /** px — max width of the welcome card. */
      cardMaxWidth: number;
      /** px — diameter of the circular logo badge. */
      logoBadgeSize: number;
      /** px — display size of the Logo glyph inside the badge. */
      logoSize: number;
      /** Hero title type scale. */
      title: { fontSize: { xs: string; sm: string }; lineHeight: number; letterSpacing: string };
      /** Welcome-message body text. */
      body: { fontSize: string; lineHeight: number };
      /** "Connected as" meta line. */
      meta: { fontSize: string; lineHeight: number };
    };
  }

  interface ThemeOptions {
    toolbar?: { height?: number };
    navigation?: {
      main?: {
        hover?: string;
        selected?: string;
        selectedAccent?: string;
        selectedBorder?: string;
      };
      subMenu?: {
        hover?: string;
        selected?: string;
        selectedAccent?: string;
        selectedBorder?: string;
      };
    };
    avatar?: {
      sm?: number;
      md?: number;
      lg?: number;
    };
    iconSize?: {
      xxs?: number;
      xs?: number;
      sm?: number;
      md?: number;
      lg?: number;
    };
    chip?: {
      sm?: number;
    };
    button?: {
      sm?: number;
      md?: number;
    };
    searchField?: {
      width?: number;
      height?: number;
      fontSize?: string;
      color?: string;
    };
    subNav?: {
      indicatorHeight?: number;
      size?: Record<
        'medium' | 'small',
        { fontSize: string; px: number; py: number; minHeight: number | 'auto' }
      >;
    };
    borderRadius?: {
      pill?: string;
      xs?: string;
      sm?: string;
      md?: string;
      lg?: string;
      xl?: string;
    };
    detailPage?: {
      containerPx?: number;
      containerPt?: number;
      containerPb?: number;
      gap?: number;
      gridCols?: { xs: string; lg: string };
      tableMaxHeight?: number;
      historyTableMaxHeight?: number;
    };
    filters?: {
      operatorSelectWidth?: number;
      chipColor?: string;
      chipBgColor?: string;
    };
    authCard?: {
      maxWidth?: number;
      logoWidth?: number;
    };
    homePage?: {
      cardMaxWidth?: number;
      logoBadgeSize?: number;
      logoSize?: number;
      title?: { fontSize: { xs: string; sm: string }; lineHeight: number; letterSpacing: string };
      body?: { fontSize: string; lineHeight: number };
      meta?: { fontSize: string; lineHeight: number };
    };
  }

  interface TypeText {
    label: string;
    body: string;
    /** Slate tone for secondary emphasis (e.g. an inline name). */
    muted: string;
    /** Faint slate tone for de-emphasized meta text. */
    subtle: string;
  }

  interface TypeBackground {
    /** Maquette page background for fiche / detail screens (e.g. the user & profile fiches). */
    page: string;
    /** Maquette section-header / panel surface, one step up from `page` (e.g. card headers). */
    section: string;
  }
}

const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline': {
            borderColor: '#3C71A4',
          },
        },
      },
    },
  },
  shape: { borderRadius: 4 },
  borderRadius: { pill: '9999px', xs: '6px', sm: '8px', md: '12px', lg: '16px', xl: '18px' },
  toolbar: { height: 48 },
  avatar: { sm: 32, md: 40, lg: 55 },
  iconSize: { xxs: 12, xs: 14, sm: 20, md: 24, lg: 56 },
  chip: { sm: 22 },
  button: { sm: 30, md: 44 },
  searchField: { width: 250, height: 32, fontSize: '13.5px', color: '#0F172A' },
  subNav: {
    indicatorHeight: 2,
    size: {
      medium: { fontSize: '1.125rem', px: 2, py: 2, minHeight: 'auto' },
      small: { fontSize: '0.8125rem', px: 1.25, py: 1, minHeight: 36 },
    },
  },
  detailPage: {
    containerPx: 3,
    containerPt: 3,
    containerPb: 4,
    gap: 3,
    gridCols: { xs: '1fr', lg: 'repeat(2, 1fr)' },
    tableMaxHeight: 385,
    historyTableMaxHeight: 250,
  },
  filters: { operatorSelectWidth: 160, chipColor: '#335C83', chipBgColor: '#E6F7FB' },
  authCard: { maxWidth: 400, logoWidth: 200 },
  homePage: {
    cardMaxWidth: 620,
    logoBadgeSize: 74,
    logoSize: 46,
    title: { fontSize: { xs: '1.625rem', sm: '1.875rem' }, lineHeight: 1.2, letterSpacing: '-0.4px' },
    body: { fontSize: '0.9375rem', lineHeight: 1.55 },
    meta: { fontSize: '0.8125rem', lineHeight: 1.6 },
  },
  typography: {
    fontWeightSemiBold: 600,
    fontSizeTitle: 15,
    fontSizeDescription: 13,
    fontSizeLabel: 11,
    subtitle1: {
      fontWeight: 600,
      fontSize: '1.0625rem', // 17px — section card titles
    },
    breadcrumbLink: { fontSize: '1.25rem' }, // 20px — breadcrumb navigation link
    navTab: { fontSize: '1.125rem' }, // 18px — detail page nav tabs
    employeeName: { fontSize: '1.25rem' }, // 20px — employee name in sub-header
  },

  palette: {
    primary: {
      light: '#4A7BA7',
      main: '#3C71A4',
      dark: '#3C71A4',
    },
    common: {
      white: '#FFFFFF',
    },
    background: {
      // Maquette page background for fiche / detail screens.
      page: '#F5F7FA',
      // Maquette section-header / panel surface.
      section: '#F1F4F8',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#4A7BA7',
      label: '#000000A6', // 65% black → ~#595959 sur blanc — contraste ~7:1 (WCAG AAA)
      body: '#1F2937', // gris foncé — contraste ~13:1 sur blanc (WCAG AAA)
      muted: '#475569', // slate 600 — secondary emphasis (noms, valeurs)
      subtle: '#94a3b8', // slate 400 — texte meta discret
    },
    action: {
      hover: 'rgba(255, 255, 255, 0.8)',
      selected: 'rgba(255, 255, 255, 1)',
      disabledOpacity: 0.45,
    },
  },

  navigation: {
    main: {
      hover: '#E5E7EB',
      selected: '#C0FBFF', // TO VALIDATE: to use darker #0891B2
      selectedAccent: '#0891B2',
      selectedBorder: '#0891B2', // left border
    },
    subMenu: {
      hover: '#E5E7EB',
      selected: '#C0FBFF',
      selectedAccent: '#0891B2',
      selectedBorder: '#0891B2',
    },
  },
});

export default theme;
